import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { getPatientFullName } from '@/api/mappers';
import { Sidebar } from '@/components/shell/Sidebar';
import { DeleteRecordButton } from '@/features/delete/DeleteRecordButton';
import {
  useSetBreadcrumbs,
  type BreadcrumbItem,
} from '@/components/shell/ShellContext';
import { PATIENT_RECORD_SECTIONS } from '@/features/patient-record/sections.config';
import { useUnsavedNavGuard } from '@/features/patient-record/hooks/useUnsavedNavGuard';
import type { PatientViewModel } from '@/models/patient';

import { BreakGlassBadge } from './BreakGlassBadge';
import { BreakGlassGate } from './BreakGlassGate';
import { ExportMenu } from './ExportMenu';
import { GeneticSummaryStrip } from './GeneticSummaryStrip';
import { PatientIdentityBand } from './PatientIdentityBand';
import { PatientSidePanels } from './PatientSidePanels';
import { PatientSubTabs } from './PatientSubTabs';
import { SubsectionProvider, type SubsectionContextValue } from './SubsectionContext';
import { TasksChip } from './PatientHeader/TasksChip';
import { patientSidebarGroups } from './patientSidebar';
import { AutosaveToggle } from './_shared/AutosaveToggle';
import { SaveButton } from './_shared/SaveButton';
import { CardBroadcastProvider } from './_shared/cardBroadcast';
import { CardViewStateControl, type CardViewState } from './_shared/cardViewState';
import { UnsavedChangesDialog } from './_shared/UnsavedChangesDialog';

interface PatientRecordViewProps {
  readonly patient: PatientViewModel;
}

const DEFAULT_SECTION = PATIENT_RECORD_SECTIONS[0];

/**
 * Loaded-state view for the patient record. V1 "Refined Spine" model:
 * sidebar selects a major section (Demographics, Phenotypes, …) → only
 * that section renders → sub-tabs anchor regions inside it.
 *
 * Cards inside a subsection-bearing section can be open simultaneously.
 * `SubsectionContext` holds the open set; the tab strip uses it for
 * indicator dots and for jump-and-open clicks. Cards reset to all-open
 * when the user switches major sections.
 */
export function PatientRecordView({ patient }: PatientRecordViewProps): ReactElement {
  const { t } = useTranslation();
  const { hash } = useLocation();
  const navigate = useNavigate();
  const fullName = getPatientFullName(patient) ?? t('patient.header.unknownName');
  const scrollerRef = useRef<HTMLDivElement | null>(null);

  const onPatientDeleted = useCallback(() => {
    void navigate('/browse/patients');
  }, [navigate]);

  const activeId = hash.length > 1 ? hash.slice(1) : (DEFAULT_SECTION?.id ?? '');
  const activeSection =
    PATIENT_RECORD_SECTIONS.find((s) => s.id === activeId) ?? DEFAULT_SECTION;
  const subsections = activeSection?.subsections;

  // Open state for managed cards. Defaults to just the first subsection
  // open — keeps the section easy to scan on land; the user expands more
  // cards via chevron or tab clicks. Resets on each major-section switch
  // via the effect below.
  const [openIds, setOpenIds] = useState<ReadonlySet<string>>(() => {
    const first = subsections?.[0]?.id;
    return new Set(first !== undefined ? [first] : []);
  });
  useEffect(() => {
    const first = subsections?.[0]?.id;
    setOpenIds(new Set(first !== undefined ? [first] : []));
  }, [subsections]);

  // Switching major sections from the sidebar lands the user at the top of
  // the new section, rather than carrying over the previous section's scroll
  // position (PTV2-1206).
  useEffect(() => {
    scrollerRef.current?.scrollTo({ top: 0 });
  }, [activeId]);

  const setOpen = useCallback((anchorId: string, open: boolean): void => {
    setOpenIds((prev) => {
      const has = prev.has(anchorId);
      if (open === has) return prev;
      const next = new Set(prev);
      if (open) next.add(anchorId);
      else next.delete(anchorId);
      return next;
    });
  }, []);

  const scrollTo = useCallback((anchorId: string): void => {
    const scroller = scrollerRef.current;
    if (scroller === null) return;
    const target = scroller.querySelector<HTMLElement>(`#${anchorId}`);
    if (target === null) return;
    // Scroll ONLY this container. `scrollIntoView` walks up and aligns the
    // target to the top of every ancestor scrollport (and the viewport),
    // which dragged the whole shell — nav, sidebar, header — up (PTV2-1206).
    const GAP = 16; // px — mirrors the anchors' `scroll-mt-4`
    const delta =
      target.getBoundingClientRect().top - scroller.getBoundingClientRect().top;
    scroller.scrollTo({ top: scroller.scrollTop + delta - GAP, behavior: 'smooth' });
  }, []);

  const subsectionCtx = useMemo<SubsectionContextValue | null>(() => {
    if (subsections === undefined || subsections.length === 0) return null;
    return {
      openIds,
      managedIds: new Set(subsections.map((s) => s.id)),
      setOpen,
      scrollTo,
    };
  }, [subsections, openIds, setOpen, scrollTo]);

  // Global "set all cards" control (segmented radio in the identity band). Picking a state bumps a
  // nonce that every card below the CardBroadcastProvider watches; cards adopt the state but can
  // still be changed individually until the next global pick. Mirrors NodeEditorPanel's host header.
  // Defaults to 'edit' (cards open/expanded), matching the section's land-state intent.
  const [globalViewState, setGlobalViewState] = useState<CardViewState>('edit');
  const [broadcastNonce, setBroadcastNonce] = useState(0);
  const setGlobalView = useCallback((state: CardViewState): void => {
    setGlobalViewState(state);
    setBroadcastNonce((n) => n + 1);
  }, []);
  const cardBroadcast = useMemo(
    () => ({ nonce: broadcastNonce, state: globalViewState }),
    [broadcastNonce, globalViewState],
  );

  const trail = useMemo<readonly BreadcrumbItem[]>(
    () => [
      { label: t('shell.breadcrumbs.allPatients'), to: '/browse/patients' },
      { label: fullName },
    ],
    [t, fullName],
  );
  useSetBreadcrumbs(trail);

  // PTV2-1301: hold a section switch (or leaving the record) while an inline form / card on this
  // page has unsaved data, and prompt Save / Discard / Keep editing.
  const navGuard = useUnsavedNavGuard();

  const sectionContent = activeSection?.render(patient);

  return (
    // BreakGlassGate is keyed on the patient guid so switching to another
    // record — or leaving and returning to this one — remounts the gate
    // and re-prompts, per the visit-scoped memory contract (PTV2-1183).
    // Wraps the entire record so nothing (not even the header band with
    // name/DOB) renders behind the modal on a sensitive record.
    <BreakGlassGate key={patient.guid} patient={patient}>
      <div className="flex h-full">
        <Sidebar groups={patientSidebarGroups(patient.guid, t)} />
        <main
          aria-label={t('routes.patient.mainAriaLabel', { name: fullName })}
          className="flex-1 flex flex-col min-w-0 min-h-0 overflow-clip"
        >
          <PatientIdentityBand
            patient={patient}
            statusSlot={<BreakGlassBadge sensitivity={patient.sensitivity} />}
            actionSlot={
              <>
                <SaveButton scopeId={null} className="mr-1" />
                <AutosaveToggle />
                <CardViewStateControl
                  variant="segmented"
                  value={globalViewState}
                  // PTV2-1301: collapsing cards unmounts open inline forms, so guard the toggle the
                  // same way as navigation when there's unsaved work.
                  onChange={(next) => { navGuard.guard(() => { setGlobalView(next); }); }}
                  className="h-6"
                />
                <TasksChip guid={patient.guid} />
                <ExportMenu guid={patient.guid} />
                <DeleteRecordButton
                  targetType="PATIENT"
                  guid={patient.guid}
                  entityLabel={fullName}
                  onDeleted={onPatientDeleted}
                />
              </>
            }
          />
          <GeneticSummaryStrip guid={patient.guid} />

          <UnsavedChangesDialog
            open={navGuard.blocked}
            canSave={navGuard.canSave}
            summaries={navGuard.summaries}
            onSave={navGuard.onSave}
            onDiscard={navGuard.onDiscard}
            onKeepEditing={navGuard.onKeepEditing}
          />

          <SubsectionProvider value={subsectionCtx}>
            {subsections !== undefined && subsections.length > 1 && (
              <PatientSubTabs subsections={subsections} />
            )}

            <div
              ref={scrollerRef}
              className="flex-1 min-h-0 overflow-y-auto overscroll-contain bg-background"
              aria-label={t('routes.patient.recordAriaLabel')}
            >
              <div className="px-8 py-4 grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_320px] gap-6 max-w-[1400px] mx-auto items-start">
                {/* The global view control broadcasts to every card in the section column. */}
                <CardBroadcastProvider value={cardBroadcast}>
                  {/* Trailing space so a tab click can scroll even the last
                      subsection to the top of the frame (PTV2-1206). Desktop
                      only — on mobile the stacked side panels already provide
                      the room, and padding here would gap them off. */}
                  <div className="space-y-3 min-w-0 xl:pb-[calc(100vh-10rem)]">
                    {sectionContent}
                  </div>
                </CardBroadcastProvider>
                <div className="xl:sticky xl:top-4 xl:self-start xl:max-h-[calc(100vh-6rem)] xl:overflow-y-auto">
                  <PatientSidePanels patient={patient} />
                </div>
              </div>
            </div>
          </SubsectionProvider>
        </main>
      </div>
    </BreakGlassGate>
  );
}
